// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Modal } from "react-bootstrap";

import { isSet } from "utils";
import { ActionType, IAction, IActionChangeOutput, raiseAction } from "actions";
import { DataState } from "types";
import { IOutput, OutputCmd } from "parameter";
import { IState } from "states/states";
import { ModalPanel } from "states/guistates";
import { IStateDataOutput } from "states/datastates";

import Output, { IOState } from "model/output";
import SaveFeedback from "view/comp/SaveFeedback";
import { makeCommandSeqNr } from "guiutils";

type IModalOutputItemProps = {
  index: number | undefined;
  id: number | undefined;
  isOpen: boolean;
  dataState: DataState;
  oldValue: IStateDataOutput | undefined;
  parameter: IOutput | undefined;
};

const mapStateToProps = (state: IState) => {
  const parameter = isSet(state.gui.modalOutput)
    ? state.parameter!.output[state.gui.modalOutput]
    : undefined;
  const oldValue = isSet(state.gui.modalOutput)
    ? state.data.output[state.gui.modalOutput]
    : undefined;
  const props: IModalOutputItemProps = {
    index: state.gui.modalOutput,
    id: state.gui.modalOutput,
    isOpen: state.gui.modal.panel === ModalPanel.OUTPUT && isSet(parameter),
    dataState: state.gui.modal.dataState,
    oldValue,
    parameter,
  };
  return props;
};

interface IModalOutputState {
  wasOpen: boolean;
}

interface IModalOutputItemDispatch {
  onCmdClick: (index: number, cmd: OutputCmd) => void;
  onCancelClick: () => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IModalOutputItemDispatch = {
    onCmdClick: (index: number, cmd: OutputCmd) => {
      const payload: IActionChangeOutput = {
        commandSeqNr: makeCommandSeqNr(),
        index,
        cmd,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_OUTPUT_REQ, payload));
    },
    onCancelClick: () => {
      dispatch(raiseAction(ActionType.ACT_MODAL_CANCEL));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class ModalOutputItemComponent extends React.Component<
  IModalOutputItemProps & IModalOutputItemDispatch & WrappedComponentProps,
  IModalOutputState
> {
  state: IModalOutputState = {
    wasOpen: false,
  };

  componentDidUpdate() {
    // check for changes in old value
    if (this.props.isOpen !== this.state.wasOpen) {
      this.setState({
        wasOpen: this.props.isOpen,
      });
    }
  }

  public render() {
    if (!this.props.isOpen || this.props.id === undefined) return <></>; // avoid rendering of inexisting data

    return (
      <Modal show={this.props.isOpen} onHide={this.props.onCancelClick}>
        <Modal.Header closeButton>
          <FormattedMessage id="Output Settings" />
        </Modal.Header>
        <Modal.Body>{this.renderSettings()}</Modal.Body>
        <Modal.Footer>
          <SaveFeedback dataState={this.props.dataState} />
          <Button
            id="cancelOutput"
            variant="secondary"
            className="me-1"
            onClick={this.props.onCancelClick}
          >
            <FormattedMessage id="Cancel" />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  private renderSettings() {
    const oldValueModel = new Output(this.props.parameter!, this.props.oldValue);
    const oldValue =
      oldValueModel.state === IOState.ON ? (
        <FormattedMessage id="On" />
      ) : oldValueModel.state === IOState.OFF ? (
        <FormattedMessage id="Off" />
      ) : (
        <FormattedMessage id="Unknown/Offline" />
      );
    const { cmd, text } = oldValueModel.isOverride
      ? oldValueModel.state === IOState.ON
        ? { cmd: OutputCmd.OVERRIDE_OFF, text: <FormattedMessage id="Turn to Override Off" /> }
        : { cmd: OutputCmd.OVERRIDE_ON, text: <FormattedMessage id="Turn to Override On" /> }
      : oldValueModel.state === IOState.ON
      ? { cmd: OutputCmd.OFF, text: <FormattedMessage id="Switch Off" /> }
      : { cmd: OutputCmd.ON, text: <FormattedMessage id="Switch On" /> };

    return (
      <>
        <p>
          <FormattedMessage id="Changing the settings of this output item:" />
        </p>
        <table>
          <tbody>
            <tr>
              <td>
                <FormattedMessage id="Current Value" />:
              </td>
              <td>
                {oldValueModel.isOverride && (
                  <>
                    <FormattedMessage id="Override" />{" "}
                  </>
                )}
                {oldValue}
              </td>
            </tr>
            <tr>
              <td>
                <FormattedMessage id="Command" />:
              </td>
              <td>
                <Button
                  id="saveOutput"
                  variant="primary"
                  onClick={this.props.onCmdClick.bind(this, this.props.index!, cmd)}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                >
                  {text}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="mt-2">
          <FormattedMessage id="The new command will be executed immediately." />
        </p>
      </>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(ModalOutputItemComponent));
